/**
 * Helpers for Trip Create / Edit — mirrors iOS EditTripView validation & save diffs.
 */

import type { Trip, TripCityVisitMeta } from '../types/client';
import { isAtSeaLocation, addDaysToIsoDate } from '../services/cruiseItinerariesApi';
import { tripEditEn } from '../content/trips.en';

export function cloneTrip(trip: Trip): Trip {
  return JSON.parse(JSON.stringify(trip)) as Trip;
}

export function todayIsoLocal(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function emptyMeta(partial?: Partial<TripCityVisitMeta>): TripCityVisitMeta {
  return {
    arrivalDate: null,
    departureDate: null,
    portArrivalHm: null,
    portDepartureHm: null,
    cruisePortStatus: null,
    dockNumber: null,
    isEmbarkation: false,
    isDebarkation: false,
    aiJobId: null,
    aiJobStartedAt: null,
    aiStatus: null,
    aiUpdatedAt: null,
    ...partial,
  };
}

/** Sorted city ids by arrival date (then name). */
export function sortedCityIds(trip: Trip): string[] {
  const ids = [...(trip.cityIds ?? [])];
  ids.sort((a, b) => {
    const aDate = trip.cityVisitMeta?.[a]?.arrivalDate ?? '';
    const bDate = trip.cityVisitMeta?.[b]?.arrivalDate ?? '';
    if (aDate !== bDate) return aDate.localeCompare(bDate);
    const aName = trip.cityNames?.[a] ?? '';
    const bName = trip.cityNames?.[b] ?? '';
    return aName.localeCompare(bName);
  });
  return ids;
}

export function deriveTripDatesFromCities(trip: Trip): { startDate: string | null; endDate: string | null } {
  const dates: string[] = [];
  for (const id of trip.cityIds ?? []) {
    const meta = trip.cityVisitMeta?.[id];
    if (meta?.arrivalDate) dates.push(meta.arrivalDate);
    if (meta?.departureDate) dates.push(meta.departureDate);
  }
  dates.sort();
  if (dates.length === 0) return { startDate: trip.startDate, endDate: trip.endDate };
  return { startDate: dates[0], endDate: dates[dates.length - 1] };
}

function hmToMinutes(hm: string | null | undefined): number | null {
  if (!hm) return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(hm.trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]);
}

/** Compare two YYYY-MM-DD + optional HH:mm as sortable keys. */
function visitSortKey(date: string | null, hm: string | null): string {
  const d = date ?? '';
  const minutes = hmToMinutes(hm);
  const t = minutes != null ? String(minutes).padStart(4, '0') : '0000';
  return `${d}T${t}`;
}

export function validateTripDraft(trip: Trip): string | null {
  if (!trip.title.trim()) return 'title';
  const ids = trip.cityIds ?? [];
  for (const id of ids) {
    const cityName = trip.cityNames?.[id]?.trim() ?? '';
    const isPlaceholderName =
      cityName === tripEditEn.cityNamePlaceholder ||
      cityName === tripEditEn.portNamePlaceholder;
    if (!isAtSeaLocation(cityName) && (!cityName || isPlaceholderName)) return 'cityName';

    const meta = trip.cityVisitMeta?.[id];
    const arrival = meta?.arrivalDate;
    const departure = meta?.departureDate ?? arrival;
    if (arrival && departure && departure < arrival) return 'dates';
  }
  // Overlap check (sorted by arrival)
  const ordered = sortedCityIds(trip);
  for (let i = 0; i < ordered.length; i++) {
    for (let j = i + 1; j < ordered.length; j++) {
      const a = trip.cityVisitMeta?.[ordered[i]];
      const b = trip.cityVisitMeta?.[ordered[j]];
      if (!a?.arrivalDate || !b?.arrivalDate) continue;
      const aEnd = a.departureDate ?? a.arrivalDate;
      const bStart = b.arrivalDate;
      if (aEnd < bStart) continue;
      // Overlap or same-day — for cruise same-day compare times
      if (a.arrivalDate === b.arrivalDate && aEnd === b.arrivalDate) {
        const aDep = visitSortKey(a.departureDate ?? a.arrivalDate, a.portDepartureHm);
        const bArr = visitSortKey(b.arrivalDate, b.portArrivalHm);
        if (aDep <= bArr) continue;
        return 'overlap';
      }
      if (aEnd >= bStart && a.arrivalDate <= (b.departureDate ?? b.arrivalDate)) {
        return 'overlap';
      }
    }
  }
  return null;
}

export function cityRosterChanged(original: Trip, draft: Trip): boolean {
  const a = [...(original.cityIds ?? [])].map((id) => id.toLowerCase()).sort();
  const b = [...(draft.cityIds ?? [])].map((id) => id.toLowerCase()).sort();
  if (a.length !== b.length) return true;
  return a.some((id, i) => id !== b[i]);
}

export function removedCityVisitIds(original: Trip, draft: Trip): string[] {
  const next = new Set((draft.cityIds ?? []).map((id) => id.toLowerCase()));
  return (original.cityIds ?? []).filter((id) => !next.has(id.toLowerCase()));
}

/**
 * Cities that need AI replan after edit save — mirrors iOS determineCitiesNeedingPlansById.
 */
export function citiesNeedingAiReplan(original: Trip, draft: Trip): string[] {
  const originalIds = new Set((original.cityIds ?? []).map((id) => id.toLowerCase()));
  const needing: string[] = [];

  for (const id of draft.cityIds ?? []) {
    const name = draft.cityNames?.[id] ?? '';
    if (isAtSeaLocation(name)) continue;

    const draftMeta = draft.cityVisitMeta?.[id];
    const origMeta = original.cityVisitMeta?.[id];
    const origName = original.cityNames?.[id] ?? '';

    if (!originalIds.has(id.toLowerCase()) || !origMeta) {
      needing.push(id);
      continue;
    }

    if (name.trim() !== origName.trim()) {
      needing.push(id);
      continue;
    }

    if (
      (draftMeta?.arrivalDate ?? null) !== (origMeta.arrivalDate ?? null) ||
      (draftMeta?.departureDate ?? null) !== (origMeta.departureDate ?? null) ||
      (draftMeta?.portArrivalHm ?? null) !== (origMeta.portArrivalHm ?? null) ||
      (draftMeta?.portDepartureHm ?? null) !== (origMeta.portDepartureHm ?? null)
    ) {
      needing.push(id);
    }
  }
  return needing;
}

function lastCityDeparture(trip: Trip): string {
  const ordered = sortedCityIds(trip);
  if (ordered.length === 0) return todayIsoLocal();
  const last = ordered[ordered.length - 1];
  const meta = trip.cityVisitMeta?.[last];
  return meta?.departureDate ?? meta?.arrivalDate ?? todayIsoLocal();
}

/** Default meta for a new non-cruise city (next day 11:00 → day-after 11:00). */
export function defaultCityMeta(trip: Trip): TripCityVisitMeta {
  const base = addDaysToIsoDate(lastCityDeparture(trip), 1);
  const end = addDaysToIsoDate(base, 1);
  return emptyMeta({
    arrivalDate: base,
    departureDate: end,
    portArrivalHm: '11:00',
    portDepartureHm: '11:00',
  });
}

/** Default meta for a new cruise port (same-day 09:00–17:00, docked). */
export function defaultPortMeta(trip: Trip): TripCityVisitMeta {
  const day = addDaysToIsoDate(lastCityDeparture(trip), 1);
  return emptyMeta({
    arrivalDate: day,
    departureDate: day,
    portArrivalHm: '09:00',
    portDepartureHm: '17:00',
    cruisePortStatus: 'docked',
  });
}

/** Default meta for a sea day. */
export function defaultSeaDayMeta(trip: Trip): TripCityVisitMeta {
  const day = addDaysToIsoDate(lastCityDeparture(trip), 1);
  return emptyMeta({
    arrivalDate: day,
    departureDate: day,
    portArrivalHm: '00:00',
    portDepartureHm: '23:59',
    cruisePortStatus: 'cruising',
  });
}

export function addCityToDraft(
  trip: Trip,
  name: string,
  meta: TripCityVisitMeta
): Trip {
  const cityId = crypto.randomUUID();
  return {
    ...trip,
    cityIds: [...(trip.cityIds ?? []), cityId],
    cityNames: { ...(trip.cityNames ?? {}), [cityId]: name },
    cityOptions: { ...(trip.cityOptions ?? {}), [cityId]: [] },
    cityVisitMeta: { ...(trip.cityVisitMeta ?? {}), [cityId]: meta },
  };
}

export function removeCityFromDraft(trip: Trip, cityId: string): Trip {
  const cityIds = (trip.cityIds ?? []).filter((id) => id !== cityId);
  const cityNames = { ...(trip.cityNames ?? {}) };
  delete cityNames[cityId];
  const cityOptions = { ...(trip.cityOptions ?? {}) };
  delete cityOptions[cityId];
  const cityHistories = { ...(trip.cityHistories ?? {}) };
  delete cityHistories[cityId];
  const cityVisitMeta = { ...(trip.cityVisitMeta ?? {}) };
  delete cityVisitMeta[cityId];
  const cityCoords = { ...(trip.cityCoords ?? {}) };
  delete cityCoords[cityId];
  return { ...trip, cityIds, cityNames, cityOptions, cityHistories, cityVisitMeta, cityCoords };
}

/** Ensure only one embarkation / one debarkation across the trip. */
export function setEmbarkDebark(
  trip: Trip,
  cityId: string,
  field: 'isEmbarkation' | 'isDebarkation',
  value: boolean
): Trip {
  const cityVisitMeta = { ...(trip.cityVisitMeta ?? {}) };
  if (value) {
    for (const id of Object.keys(cityVisitMeta)) {
      cityVisitMeta[id] = { ...cityVisitMeta[id], [field]: id === cityId };
    }
    if (!cityVisitMeta[cityId]) {
      cityVisitMeta[cityId] = emptyMeta({ [field]: true });
    } else {
      cityVisitMeta[cityId] = { ...cityVisitMeta[cityId], [field]: true };
    }
  } else if (cityVisitMeta[cityId]) {
    cityVisitMeta[cityId] = { ...cityVisitMeta[cityId], [field]: false };
  }
  return { ...trip, cityVisitMeta };
}

export function updateCityMeta(
  trip: Trip,
  cityId: string,
  patch: Partial<TripCityVisitMeta>
): Trip {
  const prev = trip.cityVisitMeta?.[cityId] ?? emptyMeta();
  return {
    ...trip,
    cityVisitMeta: {
      ...(trip.cityVisitMeta ?? {}),
      [cityId]: { ...prev, ...patch },
    },
  };
}

export function renameCity(trip: Trip, cityId: string, name: string): Trip {
  return {
    ...trip,
    cityNames: { ...(trip.cityNames ?? {}), [cityId]: name },
  };
}

/** Set or clear city coordinates from Places / geocode. */
export function setCityCoords(
  trip: Trip,
  cityId: string,
  coords: { latitude: number; longitude: number } | null
): Trip {
  const cityCoords = { ...(trip.cityCoords ?? {}) };
  if (
    !coords ||
    !Number.isFinite(coords.latitude) ||
    !Number.isFinite(coords.longitude)
  ) {
    delete cityCoords[cityId];
  } else {
    cityCoords[cityId] = {
      latitude: coords.latitude,
      longitude: coords.longitude,
    };
  }
  return { ...trip, cityCoords };
}

/** Apply a Places search hit: rename city + set coordinates. */
export function applyPlaceToCity(
  trip: Trip,
  cityId: string,
  place: { name: string; latitude: number; longitude: number }
): Trip {
  const renamed = renameCity(trip, cityId, place.name.trim() || trip.cityNames?.[cityId] || '');
  return setCityCoords(renamed, cityId, {
    latitude: place.latitude,
    longitude: place.longitude,
  });
}
